import { useEffect, useState } from 'react';

import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import cx from 'classnames';
import { $getRoot } from 'lexical';

import { TemplateType } from '@cloud-wave/neon-common-lib';

import ClickableIcon from 'lib/common/components/ClickableIcon';
import Loader from 'lib/common/components/Loader';
import PopoverMenuItem from 'lib/common/components/PopoverMenuItem';
import Text from 'lib/common/components/Text';
import Search from 'lib/common/components/atoms/Search';

import { ContentProps, Template } from './TemplatedResponseTypes';

import styles from './styles/templated-response.module.scss';

const ICON_SIZE = 17;

const TemplatedResponseHeader = ({ onClose }: { onClose?: () => void }) => {
  return (
    <div className={cx(styles['template-response-picker__header'])}>
      <Text className="template-response-picker__heading" type="heading3">
        Insert a Response
      </Text>
      {onClose && <ClickableIcon aria-label="Close" icon={faXmark} size={ICON_SIZE} onClick={onClose} />}
    </div>
  );
};

const NoTemplates = () => (
  <Text type="extraSmall" className={cx(styles['template-response-picker__empty'])}>
    There aren’t any available templates to select.
  </Text>
);

const NoMatchingSearch = ({ searchValue }: { searchValue: string }) => (
  <Text type="extraSmall" className={cx(styles['template-response-picker__empty'])}>
    There aren’t any templates that match the search,{' '}
    <Text bold inline type="extraSmall">
      "{searchValue}"
    </Text>
    .
  </Text>
);

export const Content = ({
  templates,
  type,
  editor,
  isSoftphone,
  closePopoverOrModal,
  onClick,
  withHeader = true,
  loading,
}: ContentProps) => {
  const [searchValue, setSearchValue] = useState<string>('');
  const [templatesToDisplay, setTemplatesToDisplay] = useState<Template[]>(templates);
  const rootStyles = isSoftphone ? styles['template-response-picker--softphone'] : styles['template-response-picker'];
  const localSearch = (value: string) => {
    const lowerValue = value.toLowerCase();
    setSearchValue(lowerValue);
  };

  const filterArrayBySearchValue = () => {
    return templates.filter((template) => {
      const content =
        type === TemplateType.EMAIL
          ? editor.parseEditorState(template.content).read(() => $getRoot().getTextContent())
          : template.content;
      return content.toLowerCase().includes(searchValue) || template.name.toLowerCase().includes(searchValue);
    });
  };

  const handleClick = ({ content }: { content: any }) => {
    onClick(content);
    closePopoverOrModal?.();
  };

  useEffect(() => {
    setTemplatesToDisplay(searchValue.trim() ? filterArrayBySearchValue() : templates);
  }, [searchValue, templates]);

  return (
    <div
      className={cx('templated-response-container', rootStyles, {
        'templated-response-container--softphone': isSoftphone,
      })}
      onClick={(e) => e.stopPropagation()}
    >
      {withHeader && <TemplatedResponseHeader onClose={closePopoverOrModal} />}

      <Search onSearch={localSearch} size="SMALL" />

      <div className={cx(styles['template-response-picker__templates'])}>
        {loading && <Loader small={!isSoftphone} centered className="full-height" />}

        {!loading && !templates.length && <NoTemplates />}

        {!loading && templates.length > 0 && searchValue && !templatesToDisplay.length && (
          <NoMatchingSearch searchValue={searchValue} />
        )}

        {!loading &&
          templatesToDisplay.map((template) => (
            <button
              onClick={() => handleClick({ content: template.content })}
              key={template.id}
              className="no-styles-button full-width"
            >
              <PopoverMenuItem
                text={template.name}
                subtitle={
                  type === TemplateType.EMAIL
                    ? editor.parseEditorState(template.content).read(() => $getRoot().getTextContent())
                    : template.content
                }
                highlighted
              />
            </button>
          ))}
      </div>
    </div>
  );
};
